import { Fragment, memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import ToolCallSummary from "./ToolCallSummary";
import StructuredBlock from "./StructuredBlock";
import TypingDots from "./TypingDots";
import MessageActions from "./MessageActions";
import ActionBars, { groupActions } from "./ActionBars";
import useActionPanel from "@containers/Hermes/hooks/useActionPanel";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";
import { makeCompanyLinkRehypePlugin } from "@containers/Hermes/utils/companyLinks";
import { splitByTables, tableDataRows, tableHeaders, tableTitles } from "@containers/Hermes/utils/tableSegments";
import { isCountryColumn } from "@containers/Hermes/utils/countryFlag";
import { companiesOfRows, rowCompanies } from "@containers/Hermes/utils/tableSelection";
import { CountryColumnsContext, FlagCell, SelectableRow, SelectableTable, SelectableTbody, SelectableThead, TableSelectionContext } from "./TableSelection";
import type { TableSelection } from "./TableSelection";
import type { AnyToolCall, ChatMessage, ContactsOfferCompany, HermesRootState, StreamingToolCall, StructuredBlock as StructuredBlockType } from "@containers/Hermes/types";

// Company links inserted by the linkifier point at internal profile routes
// (/companies/{slug}/{company_id}); model-authored links are external URLs.
// Parse the slug/id back out so a click can be attributed to a company.
function parseCompanyHref(href: string): { slug: string; companyId: string } | null {
  const path = href.split("?")[0];
  const match = /^\/companies\/([^/]+)\/([^/]+)$/.exec(path);
  return match ? { slug: match[1], companyId: match[2] } : null;
}

// Best-effort plain text of a react node (the anchor label), for the event.
function nodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (typeof node === "object" && "props" in node) return nodeText((node as { props?: { children?: ReactNode } }).props?.children);
  return "";
}

type KeyedBlock = { key: string; block: StructuredBlockType };

type MessageProps = {
  message: ChatMessage;
  isStreaming?: boolean;
  streamingToolCalls?: Record<string, StreamingToolCall>;
  onFollowUp?: (text: string, confirmToken?: string) => void;
  disableFollowUp?: boolean;
};

const NO_COLUMNS: ReadonlySet<number> = new Set();

const MARKDOWN_COMPONENTS: Components = {
  // A bordered, rounded card: horizontal row rules only, a quiet header band,
  // and a hover highlight so wide rows are easy to follow.
  table: SelectableTable,
  thead: SelectableThead,
  tbody: SelectableTbody,
  tr: SelectableRow,
  th: ({ children }: { children?: ReactNode }) => <th className="tw-px-3 tw-py-3 tw-text-left tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-gray-200 tw-text-xs tw-leading-[18px] tw-font-medium tw-text-gray-500">{children}</th>,
  td: FlagCell,
  code: ({ inline, children }: any) => {
    if (inline) {
      return <code className="tw-bg-gray-100 tw-text-gray-800 tw-rounded tw-px-1 tw-py-0.5 tw-text-[0.85em] tw-font-mono">{children}</code>;
    }
    return (
      <pre className="tw-bg-gray-900 tw-text-gray-100 tw-rounded-lg tw-p-4 tw-overflow-x-auto tw-text-sm tw-my-3">
        <code className="tw-font-mono">{children}</code>
      </pre>
    );
  },
  blockquote: ({ children }: { children?: ReactNode }) => <blockquote className="tw-border-l-4 tw-border-primary-purple-300 tw-pl-4 tw-text-gray-600 tw-italic tw-my-3">{children}</blockquote>,
  ul: ({ children }: { children?: ReactNode }) => <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-my-2">{children}</ul>,
  ol: ({ children }: { children?: ReactNode }) => <ol className="tw-list-decimal tw-pl-5 tw-space-y-1 tw-my-2">{children}</ol>,
  h1: ({ children }: { children?: ReactNode }) => <h1 className="tw-text-base tw-leading-6 tw-font-semibold tw-mt-6 tw-mb-2 tw-text-gray-900">{children}</h1>,
  h2: ({ children }: { children?: ReactNode }) => <h2 className="tw-text-base tw-leading-6 tw-font-semibold tw-mt-6 tw-mb-2 tw-text-gray-900">{children}</h2>,
  h3: ({ children }: { children?: ReactNode }) => <h3 className="tw-text-sm tw-leading-5 tw-font-semibold tw-mt-4 tw-mb-1 tw-text-gray-900">{children}</h3>,
};

function Message({ message, isStreaming, streamingToolCalls, onFollowUp, disableFollowUp }: MessageProps) {
  const isUser = message.role === "user";
  const activeConversationId = useSelector((state: HermesRootState) => state.hermes.activeConversationId);
  const analytics = useHermesAnalytics();
  const location = useLocation();
  // Coalesce rapid streaming tokens at a lower priority so live markdown parsing
  // stays smooth instead of re-rendering on every single delta.
  const deferredContent = useDeferredValue(message.content);

  // The company-profile page reads country/type from the URL query; forward
  // whatever the current Hermes URL carries (inherited from the page the user
  // opened Hermes from). Kept verbatim — these are already in the format the
  // profile page expects, so no normalization.
  const linkQuery = useMemo(() => {
    const current = new URLSearchParams(location.search);
    const forwarded = new URLSearchParams();
    const country = current.get("country");
    const type = current.get("type");
    if (country) forwarded.set("country", country);
    if (type) forwarded.set("type", type);
    return forwarded.toString();
  }, [location.search]);

  // Linkify company names in the answer to their profile pages. Rebuilds only
  // when the resolved company set changes (Immer gives a new array reference on
  // each `companies` event, so this stays current during streaming).
  const rehypePlugins = useMemo(() => [makeCompanyLinkRehypePlugin(message.companies ?? [], linkQuery)], [message.companies, linkQuery]);

  // Anchor renderer lives here (not in the static map) so a click on a company
  // profile link can be tracked with the current conversation id.
  const markdownComponents = useMemo<Components>(
    () => ({
      ...MARKDOWN_COMPONENTS,
      a: ({ href, children }: { href?: string; children?: ReactNode }) => {
        const company = typeof href === "string" ? parseCompanyHref(href) : null;
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="tw-text-primary-purple-600 tw-font-medium hover:tw-underline" onClick={company ? () => analytics.companyLinkClicked({ conversationId: activeConversationId, companyName: nodeText(children), companyId: company.companyId, slug: company.slug }) : undefined}>
            {children}
          </a>
        );
      },
    }),
    [analytics, activeConversationId]
  );

  const toolCallPills: AnyToolCall[] = isStreaming && streamingToolCalls ? Object.values(streamingToolCalls) : (message.tool_calls ?? []);
  const hasToolCalls = toolCallPills.length > 0;

  // Blocks are keyed by type plus that type's occurrence, rather than by array
  // index: stable across re-renders, and still unique when a turn emits two
  // blocks of the same type (two charts, say), which a bare type key would not be.
  //
  // The backend sends blocks already in display order and decides placement;
  // nothing is re-sorted here. Charts go above the answer, suggestions below
  // the copy/feedback actions, and every other card after the answer — or,
  // when the backend tied it to a table (`anchor_table`, only set when the
  // answer has several tables), directly beneath that table.
  const blockSlots = useMemo(() => {
    const seen: Record<string, number> = {};
    const slots = { above: [] as KeyedBlock[], below: [] as KeyedBlock[], suggestions: [] as KeyedBlock[], byTable: new Map<number, KeyedBlock[]>() };
    for (const block of message.structured_blocks ?? []) {
      seen[block.type] = (seen[block.type] ?? 0) + 1;
      const entry = { key: `${block.type}-${seen[block.type]}`, block };
      const anchor = (block as { anchor_table?: unknown }).anchor_table;
      // Download and contacts offers render as the action bars at the end of
      // the answer (ActionBars), not in a slot.
      if (block.type === "download_offer" || block.type === "contacts_offer" || block.type === "credit_confirmation") continue;
      if (block.type === "chart") slots.above.push(entry);
      else if (block.type === "follow_up_suggestions") slots.suggestions.push(entry);
      else if (typeof anchor === "number" && anchor >= 0) slots.byTable.set(anchor, [...(slots.byTable.get(anchor) ?? []), entry]);
      else slots.below.push(entry);
    }
    return slots;
  }, [message.structured_blocks]);

  // Only split once the turn has finished, and only when a card is tied to a
  // table. Re-segmenting on every streamed chunk would re-mount the markdown
  // repeatedly, and the cards do not arrive until the end of the turn anyway.
  // Tables with an action bar. With more than one, each bar renders directly
  // beneath its table instead of all at the end, so the user can tell which
  // table a bar acts on.
  const actionTables = useMemo(() => {
    if (isStreaming || message.id <= 0) return [];
    // Every table's actions sit directly beneath it, one table or several.
    return groupActions(message.structured_blocks ?? [], tableTitles(message.content || "").length)
      .map((group) => group.tableIndex)
      .filter((index): index is number => index !== null);
  }, [isStreaming, message.id, message.structured_blocks, message.content]);

  const segments = useMemo(() => {
    if (isStreaming || (blockSlots.byTable.size === 0 && actionTables.length === 0)) return null;
    // Keyed here rather than at render: the list is a pure function of the
    // finished content, so "table 2" identifies a segment stably without keying
    // on its array position.
    return splitByTables(message.content || "").map((segment, index) => ({
      ...segment,
      key: segment.tableIndex !== null ? `table-${segment.tableIndex}` : `prose-${index}`,
    }));
  }, [isStreaming, blockSlots, actionTables, message.content]);

  // Bars placed beneath their tables; the rest (untied ones, or a table the
  // text doesn't have) stay at the end.
  const placedActionTables = useMemo(() => actionTables.filter((index) => (segments ?? []).some((segment) => segment.tableIndex === index)), [actionTables, segments]);

  // The columns of each table whose values are country names, so their cells show a flag.
  const countryColumns = useMemo(() => {
    const out = new Map<number, Set<number>>();
    for (const segment of segments ?? []) {
      if (segment.tableIndex === null) continue;
      const columns = tableHeaders(message.content || "", segment.tableIndex).flatMap((header, index) => (isCountryColumn(header) ? [index] : []));
      if (columns.length) out.set(segment.tableIndex, new Set(columns));
    }
    return out;
  }, [segments, message.content]);

  // Rows ticked in an answer's tables, by table index. A table gets checkboxes when
  // company actions sit beneath it; the ticked rows narrow those actions to just
  // their companies (Zeplin: Hermes_Response_Actions_1).
  const [picked, setPicked] = useState<Record<number, Set<number>>>({});
  useEffect(() => setPicked({}), [message.content]);

  const selectableTables = useMemo(() => {
    const out = new Map<number, { perRow: (ContactsOfferCompany | null)[]; companies: ContactsOfferCompany[] }>();
    if (!segments) return out;
    const groups = groupActions(message.structured_blocks ?? [], tableTitles(message.content || "").length);
    for (const segment of segments) {
      if (segment.tableIndex === null) continue;
      const group = groups.find((candidate) => candidate.tableIndex === segment.tableIndex);
      const companies = group?.contacts?.companies ?? [];
      // A table gets checkboxes when something can be done with its rows: company
      // actions, or a download of them.
      if (companies.length || group?.download?.spec_token) {
        const rows = tableDataRows(segment.text);
        out.set(segment.tableIndex, { perRow: companies.length ? rowCompanies(rows, companies) : rows.map(() => null), companies });
      }
    }
    return out;
  }, [segments, message.structured_blocks, message.content]);

  const toggleRow = useCallback((table: number, row: number) => {
    setPicked((previous) => {
      const next = new Set(previous[table] ?? []);
      if (next.has(row)) next.delete(row);
      else next.add(row);
      return { ...previous, [table]: next };
    });
  }, []);
  const toggleAll = useCallback((table: number, rowCount: number) => {
    setPicked((previous) => ({ ...previous, [table]: (previous[table]?.size ?? 0) === rowCount ? new Set<number>() : new Set(Array.from({ length: rowCount }, (_, index) => index)) }));
  }, []);
  const selectionFor = (table: number): TableSelection | null => {
    const info = selectableTables.get(table);
    if (!info) return null;
    return { id: `hermes-${message.id}-table-${table}`, disabled: actionCardOpen, rowCount: info.perRow.length, selected: picked[table] ?? new Set<number>(), toggleRow: (row) => toggleRow(table, row), toggleAll: () => toggleAll(table, info.perRow.length), topBar: <ActionBars blocks={message.structured_blocks ?? []} content={message.content} conversationId={activeConversationId} messageId={message.id} tableIndex={table} selectedCompanies={selectedCompanies} selectedRows={picked} selectionBar />, bottomBar: placedActionTables.includes(table) ? <ActionBars blocks={message.structured_blocks ?? []} content={message.content} conversationId={activeConversationId} messageId={message.id} tableIndex={table} selectedRows={picked} shownCompanies={shownCompanies} inCard /> : undefined };
  };
  // The companies each table actually shows. The offer lists every company Hermes resolved for
  // the answer, which can be more than the table's rows (companies named in the prose, other
  // queries), so the default selection is narrowed to the rows on screen.
  const shownCompanies = useMemo(() => {
    const out: Record<number, ContactsOfferCompany[]> = {};
    for (const [table, info] of selectableTables) {
      if (!info.companies.length) continue;
      const all = new Set(info.perRow.map((_, index) => index));
      const list = companiesOfRows(all, info.perRow, info.companies);
      if (list.length) out[table] = list;
    }
    return out;
  }, [selectableTables]);

  const selectedCompanies = useMemo(() => {
    const out: Record<number, ContactsOfferCompany[]> = {};
    for (const [table, info] of selectableTables) {
      const rows = picked[table];
      if (rows?.size && info.companies.length) out[table] = companiesOfRows(rows, info.perRow, info.companies);
    }
    return out;
  }, [picked, selectableTables]);

  // A card tied to a table the text does not have (or shown before the split
  // runs, while streaming) falls back to after the answer rather than vanish.
  const unplacedCards = useMemo(() => {
    const placed = new Set((segments ?? []).map((segment) => segment.tableIndex).filter((index) => index !== null));
    return [...blockSlots.byTable.entries()].filter(([index]) => !placed.has(index)).flatMap(([, entries]) => entries);
  }, [segments, blockSlots]);

  // Follow-up chips are also disabled while an action card holds the composer's place.
  const { isOpen: actionCardOpen } = useActionPanel();
  const suggestionsDisabled = disableFollowUp || actionCardOpen;

  const renderBlocks = (entries: KeyedBlock[]) => entries.map(({ key, block }) => <StructuredBlock key={key} block={block} onFollowUp={onFollowUp} disabled={suggestionsDisabled} conversationId={activeConversationId} messageId={message.id} />);

  const showActions = !isUser && !isStreaming && message.id > 0 && !!activeConversationId;

  // User turn: a gray rounded bubble aligned to the right.
  if (isUser) {
    return (
      <div className="tw-flex tw-w-full tw-justify-end tw-mb-6">
        <div className="tw-max-w-[80%] tw-rounded-xl tw-bg-gray-100 tw-text-gray-900 tw-px-3 tw-py-2 tw-text-sm tw-leading-5 tw-tracking-[-0.07px] tw-whitespace-pre-wrap tw-break-words">{message.content}</div>
      </div>
    );
  }

  // Assistant turn: full-width, no bubble, no avatar. Order: chart, answer
  // (with any table-anchored cards beneath their tables), remaining cards,
  // copy/like/dislike, follow-up suggestions.
  return (
    <div className="tw-w-full tw-mb-8 tw-text-sm tw-leading-5 tw-tracking-[-0.07px] tw-text-gray-900">
      {hasToolCalls && <ToolCallSummary toolCalls={toolCallPills} isStreaming={isStreaming} />}

      {renderBlocks(blockSlots.above)}

      <div className="tw-prose tw-prose-sm tw-max-w-none tw-text-gray-900 [&_p]:tw-leading-5 [&_li]:tw-leading-5 [&_p]:tw-my-3 [&_strong]:tw-font-semibold [&_strong]:tw-text-gray-900">
        {segments ? (
          segments.map((segment) => (
            <Fragment key={segment.key}>
              <TableSelectionContext.Provider value={segment.tableIndex !== null ? selectionFor(segment.tableIndex) : null}>
                <CountryColumnsContext.Provider value={(segment.tableIndex !== null && countryColumns.get(segment.tableIndex)) || NO_COLUMNS}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={rehypePlugins} components={markdownComponents}>
                    {segment.text}
                  </ReactMarkdown>
                </CountryColumnsContext.Provider>
              </TableSelectionContext.Provider>
              {segment.tableIndex !== null && renderBlocks(blockSlots.byTable.get(segment.tableIndex) ?? [])}
              {segment.tableIndex !== null && placedActionTables.includes(segment.tableIndex) && !selectableTables.has(segment.tableIndex) ? (
                <div>
                  <ActionBars blocks={message.structured_blocks ?? []} content={message.content} conversationId={activeConversationId} messageId={message.id} tableIndex={segment.tableIndex} selectedRows={picked} shownCompanies={shownCompanies} />
                </div>
              ) : null}
            </Fragment>
          ))
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={rehypePlugins} components={markdownComponents}>
            {(isStreaming ? deferredContent : message.content) || ""}
          </ReactMarkdown>
        )}
        {/* While the turn is live, end it with the three bouncing dots — both
            before any text arrives and during lulls between chunks — instead of
            a bare blinking cursor that reads as "stuck". */}
        {isStreaming && (
          <div className="tw-mt-1">
            <TypingDots />
          </div>
        )}
      </div>

      {renderBlocks(unplacedCards)}
      {renderBlocks(blockSlots.below)}

      {showActions && activeConversationId && <MessageActions content={message.content} messageId={message.id} conversationId={activeConversationId} />}

      {renderBlocks(blockSlots.suggestions)}

      {!isStreaming && message.id > 0 && <ActionBars blocks={message.structured_blocks ?? []} content={message.content} conversationId={activeConversationId} messageId={message.id} excludeTables={placedActionTables} selectedRows={picked} shownCompanies={shownCompanies} />}
    </div>
  );
}

export default memo(Message);
